// ============================================================
// HARMONIC CHARTER — Harmonic Analysis Engine
// ============================================================
// Pure functions. No side effects. No UI imports.
//
// Analyses chords in the context of a key and returns
// harmonic function labels, borrowed chord detection,
// and secondary dominant identification.
//
// FUNCTION CLASSIFICATION:
//
//   MAJOR KEY:
//     Tonic (T):       I, III, VI
//     Subdominant (S): II, IV
//     Dominant (D):    V, VII
//
//   MINOR KEY (natural minor):
//     Tonic (T):       i, III, VI
//     Subdominant (S): ii°, iv
//     Dominant (D):    V, VII (often raised in harmonic minor)
//
//   BORROWED (from parallel mode):
//     bII, bIII, bVI, bVII in major → borrowed from minor/Mixolydian
//     #IV, #VII in minor → borrowed from major
//
//   SECONDARY DOMINANT:
//     A dominant-quality chord whose root is a fifth above
//     another diatonic degree. e.g. II7 in C major = V/V
// ============================================================

import type {
  Chord,
  Key,
  ChordAnalysis,
  HarmonicFunction,
  AnalysisSettings,
} from './types';
import type { Degree } from './types';
import { getScaleIntervals, PITCH_CLASS } from './theory';

// ------------------------------------------------------------
// DEFAULT ANALYSIS SETTINGS
// ------------------------------------------------------------

export const DEFAULT_ANALYSIS_SETTINGS: AnalysisSettings = {
  enabled:        false,
  showFunction:   true,
  showBorrowed:   true,
  showDegree:     false,
  showSecondary:  true,
};

// ------------------------------------------------------------
// DIATONIC DEGREE SETS
// ------------------------------------------------------------

// Function mapping for major key (degree → function)
const MAJOR_FUNCTION: Record<number, HarmonicFunction> = {
  1: 'T',  // I   tonic
  2: 'S',  // ii  subdominant
  3: 'T',  // iii tonic
  4: 'S',  // IV  subdominant
  5: 'D',  // V   dominant
  6: 'T',  // vi  tonic
  7: 'D',  // vii° dominant
};

// Function mapping for minor key
const MINOR_FUNCTION: Record<number, HarmonicFunction> = {
  1: 'T',  // i   tonic
  2: 'S',  // ii° subdominant
  3: 'T',  // III tonic
  4: 'S',  // iv  subdominant
  5: 'D',  // V   dominant (harmonic minor)
  6: 'T',  // VI  tonic
  7: 'D',  // VII dominant
};

// ------------------------------------------------------------
// BORROWED CHORD DETECTION
// ------------------------------------------------------------

/**
 * Returns true if a chord is borrowed from a parallel mode.
 *
 * A chord is borrowed when its root pitch class does not belong
 * to the diatonic scale of the current key.
 */
export function isBorrowed(chord: Chord, key: Key): boolean {
  const rootPc   = PITCH_CLASS[key.root];
  const intervals = getScaleIntervals(key.scale);

  // Get the pitch class of the chord root
  const degreeInterval = intervals[chord.degree];
  const accOffset = chord.accidental === 'flat' ? -1 : chord.accidental === 'sharp' ? 1 : 0;
  const chordRootPc = ((rootPc + degreeInterval + accOffset) % 12 + 12) % 12;

  // Get all diatonic pitch classes for this key
  const diatonicPcs = new Set(
    ([1, 2, 3, 4, 5, 6, 7] as Degree[]).map(
      (d) => (rootPc + intervals[d]) % 12
    )
  );

  return !diatonicPcs.has(chordRootPc);
}

// ------------------------------------------------------------
// SECONDARY DOMINANT DETECTION
// ------------------------------------------------------------

/**
 * Returns the target degree if this chord functions as a
 * secondary dominant (V of X), or null if it doesn't.
 *
 * A secondary dominant is a major or dominant-7 chord whose
 * root is a perfect fifth above a diatonic scale degree.
 *
 * Examples in C major:
 *   D major (degree 2, no accidental) = V/V (target: 5)
 *   A major (degree 6, no accidental) = V/ii (target: 2)
 *   E major (degree 3, no accidental) = V/vi (target: 6)
 */
export function getSecondaryTarget(chord: Chord, key: Key): number | null {
  // Only dominant-quality chords can be secondary dominants
  const isDominant =
    chord.quality === 'major' ||
    chord.quality === 'dominant7' ||
    chord.quality === 'dominant9' ||
    chord.quality === 'sus4';

  if (!isDominant) return null;

  const rootPc    = PITCH_CLASS[key.root];
  const intervals = getScaleIntervals(key.scale);

  // Pitch class of this chord's root
  const accOffset = chord.accidental === 'flat' ? -1 : chord.accidental === 'sharp' ? 1 : 0;
  const chordPc   = ((rootPc + intervals[chord.degree] + accOffset) % 12 + 12) % 12;

  // Check if this chord is a fifth above any diatonic degree
  // (i.e. if chordPc - 7 semitones = some diatonic degree root)
  for (const d of [1, 2, 3, 4, 5, 6, 7] as Degree[]) {
    const targetPc = (rootPc + intervals[d]) % 12;
    // Is this chord's root a perfect fifth (7 semitones) above the target?
    if (((targetPc + 7) % 12) === chordPc) {
      // Don't label V/I as secondary (that's just the dominant)
      if (d === 5 && chord.accidental === null) return null;
      return d;
    }
  }

  return null;
}

// ------------------------------------------------------------
// HARMONIC FUNCTION CLASSIFICATION
// ------------------------------------------------------------

/**
 * Returns the harmonic function of a chord in a given key.
 */
export function getFunction(chord: Chord, key: Key): HarmonicFunction {
  const borrowed = isBorrowed(chord, key);
  const fnMap    = key.scale === 'major' ? MAJOR_FUNCTION : MINOR_FUNCTION;

  if (!borrowed) {
    return fnMap[chord.degree] ?? '?';
  }

  // Borrowed chord — classify by quality
  const quality = chord.quality;
  if (quality === 'dominant7' || quality === 'major' || quality === 'dominant9') {
    return 'BD';
  }
  if (quality === 'minor' || quality === 'minor7' || quality === 'minor9') {
    return 'BT';
  }
  if (quality === 'diminished' || quality === 'halfDiminished') {
    return 'BS';
  }
  return 'BT'; // default borrowed tonic
}

// ------------------------------------------------------------
// ROMAN NUMERAL LABEL
// ------------------------------------------------------------

const ROMAN_UPPER: Record<number, string> = {
  1: 'I', 2: 'II', 3: 'III', 4: 'IV',
  5: 'V', 6: 'VI', 7: 'VII',
};

// ------------------------------------------------------------
// MAIN EXPORT: analyseChord
// ------------------------------------------------------------

/**
 * Performs full harmonic analysis on a single chord.
 * Returns a ChordAnalysis with function label, borrowed flag,
 * secondary dominant info, and a display label string.
 */
export function analyseChord(chord: Chord, key: Key): ChordAnalysis {
  const borrowed        = isBorrowed(chord, key);
  const fn              = getFunction(chord, key);
  const secondaryTarget = getSecondaryTarget(chord, key);
  const isSecondary     = secondaryTarget !== null;

  // Build display label — secondary dominants use 'SD' as the
  // HarmonicFunction value; the display string is stored separately.
  const harmonicFn: HarmonicFunction = isSecondary ? 'SD' : fn;
  const label: string = isSecondary
    ? `V/${ROMAN_UPPER[secondaryTarget!]}`
    : fn;

  return {
    function:        harmonicFn,
    borrowed,
    isSecondary,
    secondaryTarget,
    label,
  };
}

// ------------------------------------------------------------
// ANALYSE BAR
// ------------------------------------------------------------

/**
 * Analyses all chords in a bar and returns their analyses.
 * Returns null entries for empty positions.
 */
export function analyseChords(
  chords: Chord[],
  key: Key,
): ChordAnalysis[] {
  return chords.map((chord) => analyseChord(chord, key));
}

// ------------------------------------------------------------
// FUNCTION COLOURS
// Used for borrowed chord tinting
// ------------------------------------------------------------

export const FUNCTION_COLORS: Record<HarmonicFunction, string> = {
  T:  'var(--analysis-tonic)',
  S:  'var(--analysis-subdominant)',
  D:  'var(--analysis-dominant)',
  BD: 'var(--analysis-borrowed)',
  BT: 'var(--analysis-borrowed)',
  BS: 'var(--analysis-borrowed)',
  SD: 'var(--analysis-secondary)',
  SS: 'var(--analysis-secondary)',
  '?': 'var(--analysis-unknown)',
};

export const FUNCTION_TEXT_COLORS: Record<HarmonicFunction, string> = {
  T:  'var(--analysis-tonic-text)',
  S:  'var(--analysis-subdominant-text)',
  D:  'var(--analysis-dominant-text)',
  BD: 'var(--analysis-borrowed-text)',
  BT: 'var(--analysis-borrowed-text)',
  BS: 'var(--analysis-borrowed-text)',
  SD: 'var(--analysis-secondary-text)',
  SS: 'var(--analysis-secondary-text)',
  '?': 'var(--analysis-unknown-text)',
};